import { useState, useEffect, Fragment, type CSSProperties, type ReactNode } from 'react';
import { Spinner as PennySpinner, Switch as PennySwitch } from '@melio/penny';

import { useApp, Icon, type IconKey } from '@/components/ds';

// code.tsx — syntax-highlighted dark/light code blocks, copy, tabs, and misc UI atoms.

// ---- Syntax highlighter (lightweight, multi-language) -------------------
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
const KW =
  'const|let|var|function|return|import|from|require|await|async|def|class|public|private|static|void|new|if|else|elif|for|while|in|of|range|print|fmt|package|func|type|struct|interface|map|err|nil|None|True|False|true|false|null|end|do|puts|module|use|echo|throw|try|catch|with|as|lambda|go|defer|chan';
const HL_RE = new RegExp(
  '(#[^\\n]*|//[^\\n]*)' + // 1 comment
    '|("(?:[^"\\\\]|\\\\.)*"|\'(?:[^\'\\\\]|\\\\.)*\'|`(?:[^`\\\\]|\\\\.)*`)' + // 2 string
    '|\\b(\\d[\\d_]*\\.?\\d*)\\b' + // 3 number
    '|\\b(' +
    KW +
    ')\\b' + // 4 keyword
    '|(https?://[^\\s"\'<>]+)' + // 5 url
    '|\\b([A-Z][A-Za-z0-9_]+)\\b', // 6 Type/Const
  'g'
);
function highlight(code: string): string {
  let out = '',
    last = 0,
    m: RegExpExecArray | null;
  HL_RE.lastIndex = 0;
  while ((m = HL_RE.exec(code))) {
    out += escapeHtml(code.slice(last, m.index));
    const tok = m[0];
    const cls = m[1] ? 'cm' : m[2] ? 'st' : m[3] ? 'nm' : m[4] ? 'kw' : m[5] ? 'ur' : 'ty';
    out += `<span class="t-${cls}">${escapeHtml(tok)}</span>`;
    last = m.index + tok.length;
  }
  out += escapeHtml(code.slice(last));
  return out;
}

interface CodeThemeTokens {
  bg: string;
  panel: string;
  border: string;
  text: string;
  cm: string;
  st: string;
  nm: string;
  kw: string;
  ur: string;
  ty: string;
  tabbar: string;
  tabActive: string;
  tabText: string;
  tabActiveText: string;
}

const CODE_THEME: { dark: CodeThemeTokens; light: CodeThemeTokens } = {
  dark: {
    bg: '#16171f',
    panel: '#1c1e29',
    border: '#2c2f3d',
    text: '#cdd2dc',
    cm: '#5c6472',
    st: '#8fd6a8',
    nm: '#f2b574',
    kw: '#b79eff',
    ur: '#8fb4fa',
    ty: '#9fd0ff',
    tabbar: '#11121a',
    tabActive: '#1c1e29',
    tabText: '#7c879c',
    tabActiveText: '#ffffff',
  },
  light: {
    bg: '#fbfcfe',
    panel: '#fbfcfe',
    border: '#e4e7ec',
    text: '#344054',
    cm: '#7c879c',
    st: '#028838',
    nm: '#985e01',
    kw: '#5f33e6',
    ur: '#306af7',
    ty: '#2256d6',
    tabbar: '#f1f5f8',
    tabActive: '#ffffff',
    tabText: '#646f87',
    tabActiveText: '#18191b',
  },
};

interface CopyChipProps {
  text: string;
  label?: string;
  dark?: boolean;
  style?: CSSProperties;
}
function CopyChip({ text, label = 'Copy', dark = true, style }: CopyChipProps) {
  const [done, setDone] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(text);
    } catch (e) {}
    setDone(true);
    setTimeout(() => setDone(false), 1400);
  };
  const [h, setH] = useState(false);
  return (
    <button
      onClick={copy}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 30,
        padding: '0 10px',
        borderRadius: 7,
        border: `1px solid ${dark ? '#2c2f3d' : 'var(--border-static)'}`,
        background: h ? (dark ? '#242736' : 'var(--neutral-200)') : 'transparent',
        color: done ? (dark ? '#8fd6a8' : 'var(--success-700)') : dark ? '#9aa3b2' : 'var(--text-secondary)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'background-color .15s, color .15s',
        ...style,
      }}
    >
      <Icon name={done ? 'checked' : 'document'} size={14} color="currentColor" />
      {done ? 'Copied' : label}
    </button>
  );
}

interface CodeBlockProps {
  code?: string;
  lang?: string;
  langs?: Record<string, string>;
  filename?: string;
  title?: string;
  dark?: boolean;
  lineNumbers?: boolean;
  maxHeight?: number;
  style?: CSSProperties;
  headerRight?: ReactNode;
  /** Where the copy affordance sits — the header bar (default, best for multi-line/tabbed code) or
   *  next to the code itself (best for a single short value the header title otherwise sits far from). */
  copyPlacement?: 'header' | 'body';
}
// dark = override; otherwise follows app code theme
function CodeBlock({
  code,
  lang,
  langs,
  filename,
  title,
  dark,
  lineNumbers,
  maxHeight = 460,
  style,
  headerRight,
  copyPlacement = 'header',
}: CodeBlockProps) {
  const app = useApp();
  const isDark = dark != null ? dark : app ? app.codeDark : true;
  const th = isDark ? CODE_THEME.dark : CODE_THEME.light;
  // langs: { curl: '...', node: '...' } object → tabbed. else single `code`.
  const tabKeys = langs ? Object.keys(langs) : null;
  const appLang = app ? app.lang : 'curl';
  const [tab, setTab] = useState<string | null>(tabKeys ? (tabKeys.includes(appLang) ? appLang : tabKeys[0]) : null);
  useEffect(() => {
    if (tabKeys && tabKeys.includes(appLang)) setTab(appLang);
  }, [appLang]);
  const src = langs && tab ? langs[tab] : code;
  const lines = (src || '').split('\n');
  return (
    <div
      className="codetheme"
      style={{ borderRadius: 12, overflow: 'hidden', border: `1px solid ${th.border}`, background: th.bg, ...style }}
    >
      {(tabKeys || filename || title) && (
        <div
          style={{ display: 'flex', alignItems: 'center', background: th.tabbar, borderBottom: `1px solid ${th.border}`, minHeight: 42 }}
        >
          {tabKeys ? (
            <div style={{ display: 'flex', flex: 1, overflowX: 'auto' }}>
              {tabKeys.map((k) => (
                <button
                  key={k}
                  onClick={() => {
                    setTab(k);
                    if (app && app.setLang) app.setLang(k);
                  }}
                  style={{
                    padding: '0 16px',
                    height: 42,
                    background: tab === k ? th.tabActive : 'transparent',
                    border: 'none',
                    borderBottom: `2px solid ${tab === k ? 'var(--accent)' : 'transparent'}`,
                    color: tab === k ? th.tabActiveText : th.tabText,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'color .15s',
                  }}
                >
                  {LANG_LABELS[k] || k}
                </button>
              ))}
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                padding: '0 16px',
                color: th.tabText,
                fontSize: 12.5,
                fontWeight: 600,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              {filename || title}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px' }}>
            {headerRight}
            {copyPlacement === 'header' && <CopyChip text={src ?? ''} dark={isDark} />}
          </div>
        </div>
      )}
      <div style={{ position: 'relative', overflow: 'auto', maxHeight }}>
        <pre
          className="om-mono"
          style={{
            margin: 0,
            padding: '16px 18px',
            paddingRight: copyPlacement === 'body' ? 100 : undefined,
            fontSize: 13,
            lineHeight: '21px',
            color: th.text,
            tabSize: 2,
            whiteSpace: copyPlacement === 'body' ? 'pre-wrap' : undefined,
            wordBreak: copyPlacement === 'body' ? 'break-all' : undefined,
          }}
        >
          <style>{`.codetheme .t-cm{color:${th.cm};font-style:italic}.codetheme .t-st{color:${th.st}}.codetheme .t-nm{color:${th.nm}}.codetheme .t-kw{color:${th.kw}}.codetheme .t-ur{color:${th.ur}}.codetheme .t-ty{color:${th.ty}}`}</style>
          {lineNumbers ? (
            <code style={{ display: 'block' }}>
              {lines.map((ln, i) => (
                <div key={i} style={{ display: 'flex' }}>
                  <span
                    style={{
                      width: 30,
                      flexShrink: 0,
                      textAlign: 'right',
                      marginRight: 16,
                      color: th.cm,
                      userSelect: 'none',
                      opacity: 0.6,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: highlight(ln) || '&nbsp;' }} />
                </div>
              ))}
            </code>
          ) : (
            <code dangerouslySetInnerHTML={{ __html: highlight(src || '') }} />
          )}
        </pre>
        {copyPlacement === 'body' && (
          <div style={{ position: 'absolute', top: 10, right: 14 }}>
            <CopyChip text={src ?? ''} dark={isDark} />
          </div>
        )}
      </div>
    </div>
  );
}
const LANG_LABELS: Record<string, string> = {
  curl: 'cURL',
  node: 'Node.js',
  python: 'Python',
  go: 'Go',
  ruby: 'Ruby',
  php: 'PHP',
  json: 'JSON',
  bash: 'Shell',
  ts: 'TypeScript',
};

// ---- Inline code --------------------------------------------------------
interface CodeProps {
  children?: ReactNode;
  style?: CSSProperties;
}
function Code({ children, style }: CodeProps) {
  return (
    <code
      className="om-mono"
      style={{
        background: 'var(--neutral-300)',
        borderRadius: 5,
        padding: '2px 6px',
        fontSize: 12.5,
        color: 'var(--brand-900)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </code>
  );
}

// ---- Segmented control --------------------------------------------------
interface SegmentedOption {
  value?: string;
  label?: string;
  icon?: IconKey;
}
interface SegmentedProps {
  value: string;
  onChange: (value: string) => void;
  options: Array<SegmentedOption | string>;
  size?: 'small' | 'medium';
  style?: CSSProperties;
}
function Segmented({ value, onChange, options, size = 'medium', style }: SegmentedProps) {
  const h = size === 'small' ? 32 : 38;
  return (
    <div style={{ display: 'inline-flex', background: 'var(--neutral-300)', borderRadius: 9, padding: 3, gap: 2, ...style }}>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : (o.value ?? '');
        const label = typeof o === 'string' ? o : (o.label ?? '');
        const icon = typeof o === 'string' ? undefined : o.icon;
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              height: h - 6,
              padding: '0 14px',
              border: 'none',
              borderRadius: 7,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              background: active ? '#fff' : 'transparent',
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              boxShadow: active ? 'var(--shadow-400)' : 'none',
              transition: 'background-color .15s, color .15s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {icon && <Icon name={icon} size={15} color={active ? 'var(--accent)' : 'var(--icon-primary)'} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ---- Toggle -------------------------------------------------------------
// penny-backed: <Switch> from @melio/penny. Penny models the busy state as `isLoading`, so the
// hand-rolled spinner-inside-the-knob is gone; `size="small"` matches the 40x24 control we had.
interface ToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  'aria-label'?: string;
  style?: CSSProperties;
}
function Toggle({ value, onChange, disabled, loading, style, ...aria }: ToggleProps) {
  const toggle = <PennySwitch size="small" value={value} onChange={onChange} isDisabled={disabled || loading} {...aria} />;
  // Switch has no `style` prop, so wrap it when a caller supplies one.
  return style ? <span style={style}>{toggle}</span> : toggle;
}

// ---- Tracker (payment lifecycle / onboarding) ---------------------------
interface TrackerStep {
  label: string;
  sub?: string;
}
interface TrackerProps {
  steps: TrackerStep[];
  current: number;
  style?: CSSProperties;
  vertical?: boolean;
}
function Tracker({ steps, current, style, vertical }: TrackerProps) {
  // steps: [{label, sub?}], current index; states: done < current, active = current, todo > current
  if (vertical) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', ...style }}>
        {steps.map((s, i) => {
          const done = i < current,
            active = i === current;
          const c = done ? 'var(--success-700)' : active ? 'var(--accent)' : 'var(--neutral-500)';
          return (
            <div key={i} style={{ display: 'flex', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: done ? 'var(--success-700)' : active ? 'var(--accent)' : '#fff',
                    border: `2px solid ${c}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {done ? (
                    <Icon name="checked" size={14} color="#fff" />
                  ) : (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#fff' : 'var(--neutral-500)' }} />
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div
                    style={{
                      width: 2,
                      flex: 1,
                      minHeight: 26,
                      background: done ? 'var(--success-700)' : 'var(--neutral-400)',
                      margin: '2px 0',
                    }}
                  />
                )}
              </div>
              <div style={{ paddingBottom: 18 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: active || done ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                  {s.label}
                </div>
                {s.sub && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{s.sub}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', ...style }}>
      {steps.map((s, i) => {
        const done = i < current,
          active = i === current;
        const c = done ? 'var(--success-700)' : active ? 'var(--accent)' : 'var(--neutral-500)';
        return (
          <Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: done ? 'var(--success-700)' : active ? 'var(--accent)' : '#fff',
                  border: `2px solid ${c}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {done ? (
                  <Icon name="checked" size={15} color="#fff" />
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 700, color: active ? '#fff' : 'var(--neutral-600)' }}>{i + 1}</span>
                )}
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: active || done ? 'var(--text-primary)' : 'var(--text-secondary)',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                }}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  background: i < current ? 'var(--success-700)' : 'var(--neutral-400)',
                  margin: '0 8px',
                  marginBottom: 18,
                }}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ---- Spinner ------------------------------------------------------------
// penny-backed: <Spinner> from @melio/penny, which accepts a numeric size directly and carries
// its own visually-hidden label for assistive tech. `color` maps onto penny's three variants;
// `stroke` is dropped (penny owns the stroke width).
interface SpinnerProps {
  size?: number;
  color?: string;
  stroke?: number;
  label?: string;
}
// Our call sites pass CSS colors; the two that matter are the brand accent and white-on-dark.
function toSpinnerVariant(color: string): 'neutral' | 'brand' | 'inverse' {
  if (color.includes('accent') || color.includes('brand')) return 'brand';
  if (color === '#fff' || color.includes('inverse')) return 'inverse';
  return 'neutral';
}
function Spinner({ size = 18, color = 'var(--accent)', label }: SpinnerProps) {
  return <PennySpinner size={size} variant={toSpinnerVariant(color)} screenReaderText={label ?? 'Loading'} />;
}

// ---- Method badge -------------------------------------------------------
const METHOD_COLORS: Record<string, [string, string]> = {
  GET: ['var(--fill-informative-secondary)', 'var(--fill-informative-primary)'],
  POST: ['var(--fill-success-secondary)', 'var(--fill-success-primary)'],
  PATCH: ['var(--fill-warning-secondary)', 'var(--fill-warning-primary)'],
  DELETE: ['var(--fill-critical-secondary)', 'var(--fill-critical-primary)'],
  PUT: ['var(--fill-warning-secondary)', 'var(--fill-warning-primary)'],
  LOOP: ['var(--brand-100)', 'var(--brand-900)'],
};
interface MethodProps {
  m: string;
  style?: CSSProperties;
}
function Method({ m, style }: MethodProps) {
  const [bg, fg] = METHOD_COLORS[m] || ['var(--neutral-300)', 'var(--neutral-900)'];
  return (
    <span
      className="om-mono"
      style={{
        background: bg,
        color: fg,
        fontWeight: 700,
        fontSize: 11,
        padding: '3px 7px',
        borderRadius: 6,
        letterSpacing: '.02em',
        ...style,
      }}
    >
      {m}
    </span>
  );
}

// ---- Kbd ----------------------------------------------------------------
interface KbdProps {
  children?: ReactNode;
}
function Kbd({ children }: KbdProps) {
  return (
    <kbd
      className="om-mono"
      style={{
        background: 'var(--neutral-300)',
        border: '1px solid var(--border-static)',
        borderBottomWidth: 2,
        borderRadius: 5,
        padding: '1px 6px',
        fontSize: 11,
        color: 'var(--text-secondary)',
      }}
    >
      {children}
    </kbd>
  );
}

export {
  escapeHtml,
  highlight,
  CodeBlock,
  CopyChip,
  Code,
  Segmented,
  Toggle,
  Tracker,
  Spinner,
  Method,
  Kbd,
  LANG_LABELS,
  CODE_THEME,
  METHOD_COLORS,
};
